import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims } from './claims';
import { documents } from './documents';
import { organizations } from './organizations';
import { users } from './users';

// Token Definitions - Approved tokens that can be used in templates
export const tokenDefinitions = pgTable("token_definitions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Token identification
  tokenName: varchar("token_name").notNull().unique(), // e.g., "claim.claimNumber"
  displayName: varchar("display_name").notNull(), // e.g., "Claim Number"
  description: text("description"), // What this token represents
  category: varchar("category").notNull(), // claim, contact, adjuster, carrier, system, etc.
  
  // Data mapping
  dataSource: varchar("data_source").notNull(), // Table or API endpoint
  dataField: varchar("data_field"), // Specific field to pull from
  
  // Formatting
  dataType: varchar("data_type").notNull(), // string, number, date, currency, boolean
  formatPattern: varchar("format_pattern"), // e.g., "MM/DD/YYYY" for dates, "$#,##0.00" for currency
  defaultValue: text("default_value"), // Fallback if data not available
  
  // Validation
  isRequired: boolean("is_required").default(false), // Must have value to generate document
  validationRules: jsonb("validation_rules"), // Additional validation logic
  
  // Status
  status: varchar("status").default("active"), // active, deprecated, archived
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  
  createdBy: varchar("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_token_definitions_category").on(table.category),
  index("idx_token_definitions_status").on(table.status),
]);


// AI Feature #8: Natural Language Search with Embeddings
export const searchEmbeddings = pgTable("search_embeddings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Entity reference
  entityType: varchar("entity_type").notNull(), // claim, document, communication, contact
  entityId: uuid("entity_id").notNull(),
  
  // Content indexed
  content: text("content").notNull(), // The text that was embedded
  metadata: jsonb("metadata"), // Additional context (claim number, doc type, etc.)
  
  // Embedding vector
  embedding: text("embedding").notNull(), // Serialized vector (OpenAI ada-002 format)
  embeddingModel: varchar("embedding_model").default("text-embedding-3-small"),
  
  // Search optimization
  contentType: varchar("content_type"), // title, body, full_text, summary
  importance: integer("importance").default(1), // Weight for search results
  
  // Metadata
  lastUpdated: timestamp("last_updated").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_search_embeddings_entity").on(table.entityType, table.entityId),
  index("idx_search_embeddings_content_type").on(table.contentType),
]);


// Search Analytics - Track search queries for learning user intent
export const searchAnalytics = pgTable("search_analytics", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Query details
  searchQuery: text("search_query").notNull(),
  searchType: varchar("search_type").notNull(),
  
  // Context
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "set null" }),
  searchContext: varchar("search_context"),
  
  // Results
  resultCount: integer("result_count"),
  topResults: jsonb("top_results"),
  
  // User behavior
  clickedResults: jsonb("clicked_results"),
  refinedQuery: text("refined_query"),
  refinementCount: integer("refinement_count").default(0),
  
  // Satisfaction signals
  foundResult: boolean("found_result"),
  actionTaken: varchar("action_taken"),
  timeToActionMs: integer("time_to_action_ms"),
  
  // Training value
  isUsefulForTraining: boolean("is_useful_for_training").default(true),
  trainingExported: boolean("training_exported").default(false),
  
  userId: varchar("user_id").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_search_analytics_type").on(table.searchType),
  index("idx_search_analytics_claim").on(table.claimId),
  index("idx_search_analytics_user").on(table.userId),
  index("idx_search_analytics_training").on(table.isUsefulForTraining, table.trainingExported),
  index("idx_search_analytics_created").on(table.createdAt),
  index("idx_search_analytics_org").on(table.orgId),
]);


// Knowledge Base Entries — Admin-managed reference materials for RAG
export const knowledgeBaseEntries = pgTable("knowledge_base_entries", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  title: varchar("title", { length: 500 }).notNull(),
  category: varchar("category").notNull(), // policy_rules, best_practices, state_regulations, carrier_tactics, building_code, engineering_papers, manufacturer_bulletins, products, industry_standard_data, custom
  content: text("content").notNull(), // Full text content
  
  // RAG control
  isActive: boolean("is_active").default(true).notNull(),
  priority: integer("priority").default(1), // Higher = preferred in retrieval
  
  // Metadata
  tags: jsonb("tags").$type<string[]>(), // Tags for filtering
  metadata: jsonb("metadata"), // Additional context (state, peril type, etc.)
  
  // Source info (for uploaded documents)
  sourceDocumentId: uuid("source_document_id").references(() => documents.id, { onDelete: "set null" }),
  sourceFileName: varchar("source_file_name"),
  
  // Indexing status
  isIndexed: boolean("is_indexed").default(false), // Whether chunks have been generated
  chunkCount: integer("chunk_count").default(0), // Number of chunks generated
  lastIndexedAt: timestamp("last_indexed_at"),
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_knowledge_base_category").on(table.category),
  index("idx_knowledge_base_active").on(table.isActive),
  index("idx_knowledge_base_created_by").on(table.createdBy),
]);
